import { Card, CardBody, CardHeader, Colors, Icon, Text } from '@amelio/ui-library';

import type { EvaluationSummaryRow } from './LaunchSummary.types';
import type { IconProps } from '@amelio/ui-library';

import { FeedbackSettingsPills } from '@/components/FeedbackSettingsPills/FeedbackSettingsPills';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';
import {
    CYCLE_CARD_SURFACE_BG_COLOR,
    CYCLE_CARD_SURFACE_BORDER_COLOR,
    CYCLE_SECTION_SURFACE,
} from '@/pages/CycleEvaluation/components/cycleCardSurface';

type IconName = IconProps['name'];

const EVALUATION_ROW_ICON: Partial<Record<string, IconName>> = {
    [EvaluationType.SELF_EVALUATION]: 'users-profile',
    [EvaluationType.EVALUATION_360]: 'users-profile-2user',
};
const DEFAULT_EVALUATION_ROW_ICON: IconName = 'edit-clipboard-tick';

export interface EvaluationsCardProps {
    evaluations: EvaluationSummaryRow[];
}

export const EvaluationsCard = ({ evaluations }: EvaluationsCardProps) => {
    const { t } = useAmelioTranslations();

    return (
        <Card
            padding={'none'}
            className={CYCLE_SECTION_SURFACE}
            bgColor={CYCLE_CARD_SURFACE_BG_COLOR}
            borderColor={CYCLE_CARD_SURFACE_BORDER_COLOR}
            dataTestId={'launch-summary-evaluations'}
        >
            <CardHeader
                icon={
                    <Icon
                        name={'edit-clipboard-tick'}
                        color={Colors.STEP_RAIL_TURQUOISE}
                        size={16}
                    />
                }
                title={t('cycleEvaluationSchedule.launchSummary.evaluations.title')}
            />

            <CardBody>
                {evaluations.length === 0 ? (
                    <Text color={Colors.STEP_RAIL_MUTED}>
                        {t('cycleEvaluationSchedule.launchSummary.evaluations.none')}
                    </Text>
                ) : (
                    <ul className={'flex flex-col gap-3'}>
                        {evaluations.map((evaluation) => (
                            <li
                                key={evaluation.id}
                                className={[
                                    'flex items-start gap-3 rounded-lg border p-3',
                                    evaluation.isBlocked
                                        ? 'border-red-300 bg-red-50'
                                        : 'border-slate-100 bg-white/80',
                                ].join(' ')}
                                data-testid={`evaluation-row-${evaluation.id}`}
                            >
                                <Icon
                                    name={
                                        EVALUATION_ROW_ICON[evaluation.id] ??
                                        DEFAULT_EVALUATION_ROW_ICON
                                    }
                                    color={Colors.STEP_RAIL_VIOLET}
                                    size={16}
                                />

                                <div>
                                    <Text weight={'semibold'}>{t(evaluation.labelKey)}</Text>

                                    {/* Already resolved by the page: the template's own
                                        name, or the imposed "no additional template"
                                        copy — never wrapped in a second sentence. */}
                                    <Text
                                        color={Colors.STEP_RAIL_MUTED}
                                        className={'mt-[2px] text-xs'}
                                    >
                                        {evaluation.templateLabel}
                                    </Text>

                                    <FeedbackSettingsPills
                                        feedbackPrivacy={evaluation.feedbackPrivacy}
                                        commentRepliesEnabled={evaluation.commentRepliesEnabled}
                                        className={'mt-2'}
                                        dataTestId={`evaluation-row-${evaluation.id}-settings`}
                                    />

                                    {evaluation.isBlocked && (
                                        <div
                                            className={'mt-1 flex items-center gap-1.5'}
                                            data-testid={`evaluation-row-${evaluation.id}-blocked`}
                                        >
                                            <Icon
                                                name={'essential-warning-2'}
                                                color={Colors.ERROR}
                                                size={14}
                                            />

                                            <Text
                                                color={Colors.ERROR}
                                                className={'text-xs'}
                                            >
                                                {t(
                                                    'evaluation360Questionnaire.message.noQuestions',
                                                )}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </CardBody>
        </Card>
    );
};
